import { api } from "@/api/client";

export interface OAuthUrlResponse {
  url: string;
  state: string;
}

export interface GitAuthStatus {
  providers: Array<{
    provider: string;
    username: string;
    connected_at: string;
  }>;
}

export interface RepoInfo {
  provider_repo_id: string;
  name: string;
  full_name: string;
  clone_url: string;
  visibility: string;
  default_branch: string;
  size_bytes: number | null;
  language: string | null;
  description: string | null;
}

export interface DiscoverJobResponse {
  job_id: string;
  status: string;
  message: string;
  result: DiscoveryResult | null;
  error: string | null;
}

export interface LanguageInfo {
  name: string;
  file_count: number;
}

export interface DiscoveryResult {
  languages: LanguageInfo[];
  frameworks: string[];
  tools: string[];
  file_count: number;
  has_monorepo: boolean;
}

export interface BootstrapResponse {
  project_id: string;
  project_slug: string;
  repository_id: string;
  provider: string;
  stack: DiscoveryResult | null;
}

export function getOAuthUrl(provider: string, redirectUri: string) {
  return api<OAuthUrlResponse>(
    `/api/v2/git-auth/${provider}/url?redirect_uri=${encodeURIComponent(redirectUri)}`,
  );
}

export function getGitAuthStatus() {
  return api<GitAuthStatus>("/api/v2/git-auth/status");
}

export function listRepos(provider: string, search = "") {
  const params = search ? `?search=${encodeURIComponent(search)}` : "";
  return api<{ repos: RepoInfo[] }>(`/api/v2/git-auth/${provider}/repos${params}`);
}

export interface BranchesResponse {
  branches: string[];
  default_branch: string;
}

export function listBranches(provider: string, repoId: string) {
  return api<BranchesResponse>(
    `/api/v2/git-auth/${provider}/branches/${repoId}`,
  );
}

export function startDiscovery(
  provider: string,
  repoId: string,
  cloneUrl: string,
  defaultBranch: string,
  fullName = "",
) {
  return api<DiscoverJobResponse>(`/api/v2/git-auth/${provider}/discover`, {
    method: "POST",
    body: JSON.stringify({
      repo_id: repoId,
      clone_url: cloneUrl,
      full_name: fullName,
      default_branch: defaultBranch,
    }),
  });
}

export function pollDiscovery(jobId: string) {
  return api<DiscoverJobResponse>(`/api/v2/git-auth/discover/${jobId}`);
}

export function bootstrapProject(provider: string, jobId: string, projectName: string) {
  return api<BootstrapResponse>(`/api/v2/git-auth/${provider}/bootstrap`, {
    method: "POST",
    body: JSON.stringify({ job_id: jobId, project_name: projectName }),
  });
}

export interface GeneratedFile {
  path: string;
  content: string;
}

export interface GeneratedFilesResponse {
  files: GeneratedFile[];
}

export interface ConflictCheckResponse {
  has_conflicts: boolean;
  existing_files: string[];
  new_files: string[];
}

export interface PushFilesResponse {
  branch: string;
  commit_sha: string;
  pr_url: string;
  files_created: number;
  files_updated: number;
}

export function getGeneratedFiles(jobId: string) {
  return api<GeneratedFilesResponse>(`/api/v2/git-auth/generated-files/${jobId}`);
}

export function checkConflicts(provider: string, jobId: string) {
  return api<ConflictCheckResponse>(
    `/api/v2/git-auth/${provider}/check-conflicts/${jobId}`,
  );
}

export function pushFiles(provider: string, jobId: string) {
  return api<PushFilesResponse>(`/api/v2/git-auth/${provider}/push-files/${jobId}`, {
    method: "POST",
  });
}

export interface BuildGraphResponse {
  nodes_synced: number;
  edges_synced: number;
  embedding_job_id: string | null;
}

export function buildGraph(jobId: string) {
  return api<BuildGraphResponse>(`/api/v2/git-auth/build-graph/${jobId}`, {
    method: "POST",
  });
}
